import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';

/**
 * Warn, in development only, when `variant="filter"` resolves to nothing.
 *
 * **The failure this exists for.** The whole look of the filter family — the white pill, the
 * border, the active treatment — is a custom `MuiButton` variant defined in this library's theme
 * (the golden rule: customization lives in the theme). An application that has not switched its
 * root theme therefore renders every filter as a plain default MUI button. It still works. There
 * is no error, no type error, no console output, and nothing to search for. It simply looks like
 * the design system is broken, and the first thing an adopter does is file a bug against us.
 *
 * The designer and the dev team chose to keep the styling in the theme rather than make the
 * components style themselves (2026-09-22) — the alternative would have broken the one rule the
 * system is built on, and made these the only components an app cannot re-theme. This warning is
 * the other half of that decision: it turns a silent failure into a one-line fix.
 *
 * Detection is exact rather than heuristic: it asks the live theme whether a `MuiButton` variant
 * matching `variant: 'filter'` is registered. A consumer who styles the variant in their OWN
 * theme is therefore not warned, which is correct — they have solved it their way.
 */
let warned = false;

// `tsconfig.build.json` loads no `@types/node` on purpose (the emitted declarations must not
// depend on it), so `process` has to be declared here. This is the shape every bundler
// substitutes at build time; nothing of it reaches the `.d.ts`.
declare const process: { env: { NODE_ENV?: string } };

/**
 * Does this theme register the `filter` Button variant?
 *
 * Exported and pure so the DETECTION can be tested directly. The alternative — spying on
 * `console.warn` from a story — would test the plumbing rather than the logic, and would be
 * order-dependent because the warning deliberately fires only once per page.
 */
export function themeHasFilterVariant(theme: Theme): boolean {
  const variants = theme.components?.MuiButton?.variants;
  return (
    Array.isArray(variants) &&
    variants.some(entry => {
      const props = (entry as { props?: unknown }).props;
      return typeof props === 'object' && props !== null && (props as { variant?: unknown }).variant === 'filter';
    })
  );
}

export function useFilterVariantWarning(): void {
  const theme = useTheme();

  useEffect(() => {
    // `process.env.NODE_ENV`, not Vite's `import.meta.env.DEV`. The library is built with Vite
    // in library mode, and a build replaces `import.meta.env.DEV` with `false` at compile time —
    // so behind that flag the warning existed in Storybook and in no consumer's bundle, which is
    // precisely the audience it is for (review of PR #22). Library mode leaves
    // `process.env.NODE_ENV` untouched, so the CONSUMER's bundler decides: it is the same switch
    // React and MUI themselves use, their build replaces it with the app's mode, and a production
    // build still drops this block as dead code.
    if (process.env.NODE_ENV === 'production' || warned) return;

    if (themeHasFilterVariant(theme)) return;

    // Once per page, not once per trigger: a filter bar holds five of these and five identical
    // warnings would read as five problems.
    warned = true;
    // biome-ignore lint/suspicious/noConsole: intentional dev-only warning about a missing theme variant
    console.warn(
      [
        '[design-system] A filter control rendered without the `filter` Button variant, so it is',
        'showing as a plain MUI button rather than a filter trigger.',
        '',
        "The filter family is styled through this library's theme. Either make it your app's",
        'theme, or wrap the filter bar in a ThemeProvider carrying it — portalled surfaces',
        '(menus, panels) inherit it through React context, so they come out correct too:',
        '',
        "  import { ThemeProvider } from '@mui/material/styles';",
        "  import { theme } from '@openx/design-system';",
        '',
        '  <ThemeProvider theme={theme}>',
        '    <FilterBar>…</FilterBar>',
        '  </ThemeProvider>',
        '',
        'This warning is development-only and appears once per page.',
      ].join('\n')
    );
  }, [theme]);
}
